/** Small pure geo helpers. No dependencies, safe to use from the browser. */

export interface LatLon {
  readonly lat: number
  readonly lon: number
}

const EARTH_RADIUS_M = 6_371_008.8
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180

/**
 * Great-circle distance in metres. Accurate to well under a metre at the
 * distances that matter here (site matching happens inside a 200 m radius).
 */
export const haversineMeters = (a: LatLon, b: LatLon): number => {
  const dLat = toRadians(b.lat - a.lat)
  const dLon = toRadians(b.lon - a.lon)
  const lat1 = toRadians(a.lat)
  const lat2 = toRadians(b.lat)

  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * Coordinate precision used in published artifacts. Five decimals is ~1.1 m at
 * Polish latitudes - far finer than the registry's own accuracy, and it keeps
 * stations.geojson small.
 */
export const COORD_DECIMALS = 5

/** Round a coordinate to {@link COORD_DECIMALS}, normalising `-0` to `0`. */
export const roundCoord = (value: number, decimals: number = COORD_DECIMALS): number => {
  const factor = 10 ** decimals
  const rounded = Math.round(value * factor) / factor
  return rounded === 0 ? 0 : rounded
}
